import React, { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { ConfirmationDialog } from '@roger/ui-components';
import BillingPeriodCell from '../BillingPeriodCell';
import ClaimWorksheetEditor from '../ClaimWorksheetEditor';
import { ClaimWorksheetActionButton } from '../ClaimWorksheetActionButton';
import { BillingDataTable } from '../BillingDataTable';
import { BillingActionsCell } from '../BillingActionsCell';
import { BillingSelectionActionBar } from '../BillingSelectionActionBar';
import { activeBillingCycleNoteContext } from '../billingCycleNoteContext';
import { PatientCell } from '../PatientCell';
import { InsuranceCell } from '../InsuranceCell';
import { currencySortingFn } from '../billingTableUtils';
import {
  getActiveBillingCycles,
  setBillingCycleStatusReadyToSend,
  setBillingCycleStatusVoid,
} from '../../../services/api';
import type {
  ActiveBillingCycleItem,
  EpisodicClaimStatus,
} from '../../../services/api/types.gen';
import { formatDateToMMDDYYYY } from '../../../utils/dateUtils';
import { ClaimAgeCell } from '../ClaimAgeCell';
import { claimAgeDays } from '../claimAge';
import { useBillingCycles } from '../../../hooks/useBillingCycles';
import { useBillingTableQuery } from '../../../hooks/useBillingTableQuery';
import { showVoidBillingCycleFailures } from '../showVoidBillingCycleFailures';
import {
  VoidClaimsConfirmDialog,
  type VoidCopyOptions,
} from '../VoidClaimsButton';

interface EOEPendingApprovalTabProps {
  onCountsInvalidated?: () => Promise<void> | void;
}

const EOEPendingApprovalTab: React.FC<EOEPendingApprovalTabProps> = ({
  onCountsInvalidated,
}) => {
  const [worksheetEntry, setWorksheetEntry] =
    useState<ActiveBillingCycleItem | null>(null);
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [submittingAction, setSubmittingAction] = useState<
    'approve' | 'void' | null
  >(null);

  const query = useBillingTableQuery('pending-approval', {
    sortKeyByColumnId: { billing_period: 'billing_period_start' },
  });
  const {
    data,
    isLoading,
    isRefreshing,
    isSubmitting,
    selectedIds,
    selectedItems,
    total,
    totalPages,
    clearSelection,
    removeSelectedId,
    handleToggleId,
    handleToggleAll,
    mutateSelected,
    loadData,
    showError,
  } = useBillingCycles<
    ActiveBillingCycleItem,
    { statuses: EpisodicClaimStatus[] }
  >({
    tabId: 'pending-approval',
    fetchFn: getActiveBillingCycles,
    fetchPayload: { statuses: ['PENDING_APPROVAL'], ...query.toParams() },
    errorMessage: 'Could not fetch data for the "Pending Approval" tab.',
    getId: item => item.billing_cycle_id,
    getName: item => item.patient_name,
  });

  const handleConfirmApprove = () => {
    setApproveConfirmOpen(false);
    setSubmittingAction('approve');
    void mutateSelected(
      id =>
        setBillingCycleStatusReadyToSend({
          path: { billing_cycle_id: id },
        }),
      'Some billing cycles failed to approve',
      'The following billing cycles could not be updated',
      onCountsInvalidated
    ).finally(() => setSubmittingAction(null));
  };

  const handleVoid = (options: VoidCopyOptions) => {
    setSubmittingAction('void');
    void mutateSelected(
      id =>
        setBillingCycleStatusVoid({
          path: { billing_cycle_id: id },
          body: {
            copy_worksheet_forward: options.copyWorksheetForward,
            copy_notes_forward: options.copyNotesForward,
          },
        }),
      'Some billing cycles failed to void',
      'The following billing cycles could not be updated',
      onCountsInvalidated,
      failures => showVoidBillingCycleFailures(failures, showError)
    ).finally(() => setSubmittingAction(null));
  };

  const columns = useMemo<ColumnDef<ActiveBillingCycleItem>[]>(
    () => [
      {
        id: 'patient_name',
        accessorKey: 'patient_name',
        header: 'Patient',
        enableSorting: true,
        cell: ({ row }) => (
          <PatientCell
            name={row.original.patient_name}
            mrn={row.original.patient_mrn}
          />
        ),
      },
      {
        id: 'branch',
        accessorKey: 'branch',
        header: 'Clinic',
        enableSorting: true,
      },
      {
        id: 'insurance_name',
        accessorKey: 'insurance_name',
        header: 'Insurance',
        enableSorting: true,
        cell: ({ row }) => (
          <InsuranceCell
            insuranceName={row.original.insurance_name}
            mbi={row.original.mbi}
            isMedicare={row.original.is_medicare}
          />
        ),
      },
      {
        id: 'billing_period',
        header: 'Billing Period',
        accessorFn: row => row.billing_period_start,
        enableSorting: true,
        cell: ({ row }) => (
          <BillingPeriodCell
            patientId={row.original.patient_id}
            episodeId={row.original.episode_id}
            startDate={formatDateToMMDDYYYY(row.original.billing_period_start)}
            endDate={formatDateToMMDDYYYY(row.original.billing_period_end)}
          />
        ),
      },
      {
        id: 'claim_number',
        accessorKey: 'claim_number',
        header: 'Claim #',
        enableSorting: true,
      },
      {
        id: 'tob',
        accessorKey: 'tob',
        header: 'TOB',
        enableSorting: false,
      },
      {
        id: 'total_charges',
        accessorKey: 'total_charges',
        header: 'Total Charges',
        enableSorting: false,
        sortingFn: currencySortingFn,
      },
      {
        id: 'claim_age',
        header: 'Claim Age',
        accessorFn: row => claimAgeDays(row.billing_period_end) ?? -1,
        enableSorting: true,
        cell: ({ row }) => (
          <ClaimAgeCell
            periodEnd={row.original.billing_period_end}
            createdAt={row.original.created_at}
            billedAt={row.original.billed_at}
          />
        ),
      },
      {
        id: 'actions',
        header: () => <div className='text-right'>Actions</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <BillingActionsCell
            billingCycleId={row.original.billing_cycle_id}
            noteProps={{
              ...activeBillingCycleNoteContext(row.original),
              patientName: row.original.patient_name,
            }}
            leading={
              <ClaimWorksheetActionButton
                onClick={() => setWorksheetEntry(row.original)}
                label='Edit claim worksheet'
              />
            }
          />
        ),
      },
    ],
    []
  );

  const isAnyActionSubmitting = isSubmitting || submittingAction !== null;

  if (worksheetEntry !== null) {
    return (
      <ClaimWorksheetEditor
        billingCycle={worksheetEntry}
        onClose={() => setWorksheetEntry(null)}
        onApproved={async () => {
          await loadData();
          await onCountsInvalidated?.();
        }}
      />
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      <BillingDataTable
        data={data}
        columns={columns}
        getRowId={row => row.billing_cycle_id}
        query={query}
        total={total}
        totalPages={totalPages}
        isLoading={isLoading}
        isRefreshing={isRefreshing}
        selection={{
          selectedIds,
          onToggleId: handleToggleId,
          onToggleAllVisible: handleToggleAll,
        }}
      />
      <ConfirmationDialog
        open={approveConfirmOpen}
        title='Approve billing cycles?'
        message='This action cannot be undone, please make sure all information is correct'
        confirmLabel='Approve'
        onAccept={handleConfirmApprove}
        onCancel={() => setApproveConfirmOpen(false)}
      />
      <VoidClaimsConfirmDialog
        open={voidConfirmOpen}
        selectedCount={selectedIds.size}
        postSend={false}
        onConfirm={options => {
          setVoidConfirmOpen(false);
          handleVoid(options);
        }}
        onCancel={() => setVoidConfirmOpen(false)}
      />
      <BillingSelectionActionBar
        selectedItems={selectedItems}
        onClear={clearSelection}
        onRemoveSelectedId={removeSelectedId}
        actions={[
          {
            key: 'approve',
            label: 'Approve',
            disabledReason: () =>
              isAnyActionSubmitting || isLoading
                ? 'Action in progress'
                : undefined,
            run: () => setApproveConfirmOpen(true),
          },
          {
            key: 'void',
            label: 'Void',
            destructive: true,
            disabledReason: () =>
              isAnyActionSubmitting || isLoading
                ? 'Action in progress'
                : undefined,
            run: () => setVoidConfirmOpen(true),
          },
        ]}
      />
    </div>
  );
};

export default EOEPendingApprovalTab;
